import React from "react";
import { Link } from "react-router-dom";

// Kept deliberately literal: every line here has to match what the app
// actually does and what the App Store privacy label declares (User Content,
// Device ID, Email Address). If a data flow changes, change all three.
function PrivacyPolicy({ onBack }) {
  return (
    <div className="app" style={{ padding: 20, paddingBottom: 100 }}>
      <title>Privacy Policy — Between the News</title>
      <button className="detail-back" onClick={onBack}>
        {"←"} Back
      </button>

      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Privacy Policy</h1>
        <p style={{ fontSize: 12, color: "#6b6b80", marginBottom: 24 }}>Last updated: September 1, 2026</p>

        <div style={{ fontSize: 14, lineHeight: 1.8, color: "#34343f" }}>
          <Section title="1. Introduction">
            Between the News ("we", "our", "us") is an independent media-criticism
            publication based in Dublin, Ireland. This policy explains exactly what the
            Between the News app and website collect, why, and what you can ask us to
            delete. The short version: there is no account, no advertising, no tracking,
            and nothing is sold to anyone.
          </Section>

          <Section title="2. What we collect">
            <strong>Only if you choose to provide it:</strong>
            <ul style={{ paddingLeft: 20, marginTop: 8 }}>
              <li>
                <strong>Comments and headline suggestions</strong> you post — the text
                itself, and the time it was posted. These are public.
              </li>
              <li>
                <strong>Your email address</strong>, only if you type it into the newsletter
                box. It is used to send the newsletter and nothing else.
              </li>
              <li>
                <strong>A notification token</strong>, only if you turn on notifications with
                the bell icon. It identifies your device to Apple's push service so we can
                tell you when a new analysis is published. It is not linked to your name or
                email.
              </li>
            </ul>
            <br />
            <strong>Collected automatically:</strong>
            <ul style={{ paddingLeft: 20, marginTop: 8 }}>
              <li>
                Your votes and your blocked-author list are stored{" "}
                <strong>on your device only</strong> and never leave it.
              </li>
              <li>
                Ordinary server logs at our hosting providers (IP address, device type),
                kept briefly for security.
              </li>
            </ul>
            <br />
            We do <strong>not</strong> collect your name, phone number, location, contacts,
            photos, or health data. We do not ask for any device permission except
            notifications, and only when you tap the bell.
          </Section>

          <Section title="3. Analytics and advertising">
            The <strong>app contains no analytics and no advertising SDK</strong> — no
            Google Analytics, no Facebook SDK, no attribution or tracking framework of any
            kind. Our <strong>website</strong> (betweenthenews.com) uses Google Analytics
            with IP anonymisation to count visits, and only if you accept it: the banner on
            your first visit asks, nothing is loaded until you say yes, and "Cookie
            settings" in the footer lets you change your mind at any time. Declining costs
            you nothing — the site behaves identically. That code is disabled in the app
            entirely. We do not track you across other apps or websites, and we do not sell
            or share your data with data brokers.
          </Section>

          <Section title="4. Where your data is stored">
            Comments, suggestions, newsletter addresses and notification tokens are stored
            in our own Supabase (PostgreSQL) database, hosted in the European Union, with
            encryption in transit and at rest. Notifications are delivered by the Apple Push
            Notification service. Those are the only external services the app talks to,
            apart from the Google Fonts CDN, which serves a font file.
          </Section>

          <Section title="5. Moderation of what you post">
            Comments and suggestions are public and are screened before posting; anything
            containing slurs, sexual content, threats or spam is rejected. Every comment can
            be reported (reviewed within 24 hours) or blocked. Reports are stored so we can
            act on them. See our <Link to="/terms">Terms of Use</Link> for the full rules.
          </Section>

          <Section title="6. Your rights">
            You can ask us to:
            <ul style={{ paddingLeft: 20, marginTop: 8 }}>
              <li>delete a comment or suggestion you posted;</li>
              <li>remove your email address from the newsletter list;</li>
              <li>tell you what data we hold that relates to you; or</li>
              <li>stop notifications (also possible any time in your device settings).</li>
            </ul>
            <br />
            Email <strong style={{ color: "#1f6fd0" }}>privacy@betweenthenews.com</strong> and
            we will act within 30 days. As we are based in Ireland, the GDPR applies and you
            may also complain to the Irish Data Protection Commission.
          </Section>

          <Section title="7. Children's privacy">
            The app is rated 13+ and is not directed to children. We do not knowingly collect
            information from children under 13; if you believe a child has posted personal
            information, write to us and we will remove it.
          </Section>

          <Section title="8. Changes to this policy">
            If this policy changes, the date at the top changes with it, and significant
            changes are announced in the app.
          </Section>

          <Section title="9. Contact">
            Privacy questions:{" "}
            <strong style={{ color: "#1f6fd0" }}>privacy@betweenthenews.com</strong>
            <br />
            Anything else: <strong style={{ color: "#1f6fd0" }}>hello@betweenthenews.com</strong>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: "#15151e", marginBottom: 8 }}>{title}</h2>
      <div>{children}</div>
    </div>
  );
}

export default PrivacyPolicy;
